import { useEffect, useRef, useCallback } from 'react';
import LocalStorageManipulationService from '@PROJECT_ROOT/assets/responsive-frontend/js/services/LocalStorageManipulationService';
import {
    dispatchTrackingEvent,
    resetEcommerceTrackingContext,
    buildBaseAnalyticsItem,
    ADD_TO_CART,
    REMOVE_FROM_CART,
    VIEW_CART, VIEW_ITEM,
} from '@PROJECT_ROOT/assets/responsive-frontend/js/services/tracking';

const getTotalGuests = (unit) => (unit.adultsCount || 0) + (unit.children0to3Count || 0) + (unit.children4to11Count || 0) + (unit.children12to17Count || 0);

const getTotalGuestsAllUnits = (selectedUnits) => {
    if (!selectedUnits || selectedUnits.length === 0) return 0;
    return selectedUnits.reduce(
        (total, unit) => total + getTotalGuests(unit),
        0,
    );
};

const useEcommerceTracking = (
    reservationParameters,
    unitPriceCalculation,
    presentationUnit,
) => {
    const hasTriggeredViewItem = useRef(false);
    const hasTriggeredInitialAddToCart = useRef(false);
    const previousReservationParams = useRef(null);
    const previousSelectedUnits = useRef(null);

    // Provjeravamo da li imamo sve bitne podatke za praćenje
    const hasEssentialData = useCallback((params) => params?.intervalFrom && params?.intervalTo && params?.selectedUnits?.length > 0 && getTotalGuestsAllUnits(params.selectedUnits) >= 1, []);

    // Dohvaćamo podatke za praćenje jedinice
    const getUnitTrackingData = useCallback(
        (unitId) => {
            const presentationUnitData = presentationUnit?.units?.unitsShortInfo?.find((u) => u.unitId === unitId);
            if (!presentationUnitData) return null;
            // Fallback jer koristimo podatke iz dva različita izvora.
            const fallbackUnit = unitPriceCalculation?.units?.find((u) => u.unitId === unitId);
            const enrichedUnit = {
                ...presentationUnitData,
                fullTitle: presentationUnitData?.trackingUnitData?.fullTitle
                  ?? presentationUnitData?.fullTitle
                  ?? fallbackUnit?.fullTitle,
                priceWithDiscount: presentationUnitData?.priceWithDiscount ?? fallbackUnit?.priceWithDiscount,
                discount: presentationUnitData?.discount ?? fallbackUnit?.discount,
            };
            const baseAnalyticsItem = buildBaseAnalyticsItem({ unitItem: enrichedUnit });
            return {
                ...baseAnalyticsItem,
                currency: 'EUR',
                index: 0,
                item_category: 'Obiteljski odmor',
                item_list_id: 'kategory_obiteljski_odmor',
                item_list_name: 'Kategorija Obiteljski odmor',
                quantity: 1,
            };
        },
        [unitPriceCalculation, presentationUnit],
    );

    // Dohvaćamo detalje rezervacije
    const getBookingDetails = useCallback((params) => {
        if (!params.selectedUnits || params.selectedUnits.length === 0) return null;

        const fromDate = new Date(params.intervalFrom);
        const toDate = new Date(params.intervalTo);
        const nights = Math.ceil((toDate - fromDate) / (1000 * 60 * 60 * 24));

        const totalAdults = params.selectedUnits.reduce(
            (sum, unit) => sum + (unit.adultsCount || 0),
            0,
        );
        const totalChildren0to3 = params.selectedUnits.reduce(
            (sum, unit) => sum + (unit.children0to3Count || 0),
            0,
        );
        const totalChildren4to11 = params.selectedUnits.reduce(
            (sum, unit) => sum + (unit.children4to11Count || 0),
            0,
        );
        const totalChildren12to17 = params.selectedUnits.reduce(
            (sum, unit) => sum + (unit.children12to17Count || 0),
            0,
        );

        return {
            arrival_date: params.intervalFrom,
            departure_date: params.intervalTo,
            nights,
            adults: totalAdults,
            children_0_3: totalChildren0to3,
            children_4_11: totalChildren4to11,
            children_12_17: totalChildren12to17,
        };
    }, []);

    // "view_item" event
    const triggerViewItem = useCallback(() => {
        if (
            hasTriggeredViewItem.current || !unitPriceCalculation?.units?.length
        ) return;

        const selectedUnit = unitPriceCalculation.units.find((u) => u.selected) || unitPriceCalculation.units[0];

        if (!selectedUnit) return;

        const trackingData = getUnitTrackingData(selectedUnit.unitId);
        if (!trackingData) return;
        const savedItem = LocalStorageManipulationService.getTrackingItem();
        // Merge-amo stavke iz item podataka samo ako postoje i ako im je item_id isti (ne moze imati određene podatke -
        // page/index/unit_count/unit_index ako nije prošao kroz rezultate pretrage).
        const mergedTrackingData = savedItem?.item_id === trackingData.item_id
            ? {
                ...trackingData,
                page: savedItem.page ?? trackingData.page,
                index: savedItem.index ?? trackingData.index,
                unit_count: savedItem.unit_count ?? trackingData.unit_count,
                unit_index: savedItem.unit_index ?? trackingData.unit_index,
            }
            : trackingData;
        const eventData = {
            ecommerce: {
                currency: 'EUR',
                value: mergedTrackingData.price,
                items: [mergedTrackingData],
            },
        };

        resetEcommerceTrackingContext();
        dispatchTrackingEvent(VIEW_ITEM, eventData);
        hasTriggeredViewItem.current = true;
    }, [unitPriceCalculation, getUnitTrackingData]);

    // "add_to_cart" event
    const triggerAddToCart = useCallback(
        (params) => {
            if (!hasEssentialData(params)) return;

            const items = params.selectedUnits
                .map((unit) => {
                    const trackingData = getUnitTrackingData(unit.unitId);
                    return trackingData
                        ? { ...trackingData, quantity: 1 }
                        : null;
                })
                .filter(Boolean);

            if (items.length === 0) return;

            const bookingDetails = getBookingDetails(params);
            if (!bookingDetails) return;

            const totalValue = items.reduce(
                (sum, item) => sum + (item.price * item.quantity),
                0,
            );

            const eventData = {
                ecommerce: {
                    currency: 'EUR',
                    value: totalValue,
                    items,
                },
                booking_details: bookingDetails,
            };

            resetEcommerceTrackingContext();
            dispatchTrackingEvent(ADD_TO_CART, eventData);
        },
        [
            hasEssentialData,
            getUnitTrackingData,
            getBookingDetails,
        ],
    );

    // "remove_from_cart" event
    const triggerRemoveFromCart = useCallback(
        (params) => {
            if (!params?.selectedUnits || params.selectedUnits.length === 0) return;

            const items = params.selectedUnits
                .map((unit) => {
                    const trackingData = getUnitTrackingData(unit.unitId);
                    return trackingData
                        ? { ...trackingData, quantity: 1 }
                        : null;
                })
                .filter(Boolean);

            if (items.length === 0) return;

            const bookingDetails = getBookingDetails(params);
            const totalValue = items.reduce(
                (sum, item) => sum + (item.price * item.quantity),
                0,
            );

            const eventData = {
                ecommerce: {
                    currency: 'EUR',
                    value: totalValue,
                    items,
                },
                booking_details: bookingDetails,
            };

            resetEcommerceTrackingContext();
            dispatchTrackingEvent(REMOVE_FROM_CART, eventData);
        },
        [getUnitTrackingData, getBookingDetails],
    );

    // "view_cart" event
    const triggerViewCart = useCallback((params) => {
        if (!hasEssentialData(params)) return;

        const items = params.selectedUnits
            .map((unit) => {
                const trackingData = getUnitTrackingData(unit.unitId);
                if (!trackingData) return null;

                // Quantity = number of nights
                const fromDate = new Date(params.intervalFrom);
                const toDate = new Date(params.intervalTo);
                const nights = Math.ceil((toDate - fromDate) / (1000 * 60 * 60 * 24));

                return {
                    ...trackingData,
                    quantity: nights,
                };
            })
            .filter(Boolean);

        if (items.length === 0) return;

        const bookingDetails = getBookingDetails(params);
        if (!bookingDetails) return;

        const totalValue = items.reduce(
            (sum, item) => sum + (item.price * item.quantity),
            0,
        );

        const eventData = {
            ecommerce: {
                currency: 'EUR',
                value: totalValue,
                items,
            },
            booking_details: bookingDetails,
        };

        resetEcommerceTrackingContext();
        dispatchTrackingEvent(VIEW_CART, eventData);
    }, [hasEssentialData, getUnitTrackingData, getBookingDetails]);

    // Provjeravamo da li se promijenio broj gostiju
    const guestNumbersChanged = useCallback((current, previous) => {
        if (!current || !previous) return false;
        if (!current.selectedUnits || !previous.selectedUnits) return false;

        const currentTotal = getTotalGuestsAllUnits(current.selectedUnits);
        const previousTotal = getTotalGuestsAllUnits(previous.selectedUnits);

        return currentTotal !== previousTotal;
    }, []);

    // Provjeravamo da li su se promijenile odabrane jedinice
    const selectedUnitsChanged = useCallback((current, previous) => {
        if (!current || !previous) return false;
        if (!current.selectedUnits || !previous.selectedUnits) return false;

        const currentUnitIds = current.selectedUnits
            .map((unit) => unit.unitId)
            .sort();
        const previousUnitIds = previous.selectedUnits
            .map((unit) => unit.unitId)
            .sort();

        return (
            JSON.stringify(currentUnitIds) !== JSON.stringify(previousUnitIds)
        );
    }, []);

    // Provjeravamo da li su se promijenili datumi
    const datesChanged = useCallback((current, previous) => {
        if (!current || !previous) return false;

        return current.intervalFrom !== previous.intervalFrom || current.intervalTo !== previous.intervalTo;
    }, []);

    // Effect za inicijalni trigger view_item kada su svi podaci dostupni
    useEffect(() => {
        if (unitPriceCalculation && presentationUnit) {
            triggerViewItem();
        }
    }, [unitPriceCalculation, presentationUnit, triggerViewItem]);

    // Glavni Effect - sva logika na jednom mjestu
    useEffect(() => {
        // Ako nema reservationParameters odustani
        if (!reservationParameters) return;

        const currentHasEssentialData = hasEssentialData(reservationParameters);
        const previousHasEssentialData = hasEssentialData(previousReservationParams.current);

        // Provjeri da li su parametri odmah tu da opalimo add_to_cart
        if (currentHasEssentialData && unitPriceCalculation && presentationUnit && !hasTriggeredInitialAddToCart.current) {
            triggerAddToCart(reservationParameters);
            hasTriggeredInitialAddToCart.current = true;
        } else if (previousReservationParams.current && reservationParameters !== previousReservationParams.current) {
            if (previousHasEssentialData && !currentHasEssentialData) {
                triggerRemoveFromCart(previousReservationParams.current);
            } else if (currentHasEssentialData && unitPriceCalculation && presentationUnit) {
                if (
                    guestNumbersChanged(reservationParameters, previousReservationParams.current)
                    || selectedUnitsChanged(reservationParameters, previousReservationParams.current)
                    || datesChanged(reservationParameters, previousReservationParams.current)
                ) {
                    if (previousHasEssentialData) {
                        triggerRemoveFromCart(previousReservationParams.current);
                    }
                    triggerAddToCart(reservationParameters);
                }
            }
        }

        // Update previous params samo kad reservationParameters se promijene
        if (reservationParameters !== previousReservationParams.current) {
            previousReservationParams.current = reservationParameters;
        }
    }, [
        reservationParameters,
        unitPriceCalculation,
        presentationUnit,
        hasEssentialData,
        guestNumbersChanged,
        selectedUnitsChanged,
        datesChanged,
        triggerAddToCart,
        triggerRemoveFromCart,
    ]);

    useEffect(() => {
        if (reservationParameters?.selectedUnits) {
            previousSelectedUnits.current = reservationParameters.selectedUnits;
        }
    }, [reservationParameters?.selectedUnits]);

    // Return funkcije koje se mogu "ručno" koristiti u komponenti
    return {
        triggerViewItem,
        triggerAddToCart: () => triggerAddToCart(reservationParameters),
        triggerRemoveFromCart: () => triggerRemoveFromCart(reservationParameters),
        triggerViewCart: () => triggerViewCart(reservationParameters),
        hasEssentialData: () => hasEssentialData(reservationParameters),
    };
};

export default useEcommerceTracking;
